import React, {useState} from 'react';

import notifications from 'notifications';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useFormInput from 'ui-library/hooks/useFormInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {postJSONPromise} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

interface AddMCPServerModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

type TransportType = 'STREAMABLE_HTTP' | 'SSE';

export default function AddMCPServerModal(props: AddMCPServerModalProps): JSX.Element {
  const nameInput = useFormInput('');
  const serverUrlInput = useFormInput('');
  const [transportType, setTransportType] = useState<TransportType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = nameInput.value.trim() !== '' && serverUrlInput.value.trim() !== '';

  async function handleSubmit(): Promise<void> {
    try {
      setIsSubmitting(true);
      await withLoadingScreen(
        postJSONPromise({
          url: '/1/api/llm/mcp-server-configs',
          data: {
            name: nameInput.value.trim(),
            serverUrl: serverUrlInput.value.trim(),
            ...(transportType && {transportType}),
          },
        }),
        'Adding AI Connector...'
      );
      notifications.success('AI Connector added successfully');
      props.onRequestSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      title="Add AI Connector"
      onRequestSubmit={handleSubmit}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Add',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !isValid || isSubmitting,
        },
        {text: 'Cancel', cancel: true},
      ]}
    >
      <p style={{marginBottom: 16, color: 'var(--text-secondary)'}}>
        Get details from MCP servers that your team hosts or from an external tool you trust to add an AI
        connector.
      </p>
      <FormGroup>
        <TextField
          label="Name"
          placeholder="My MCP Server"
          {...nameInput}
          autoFocus={true}
          maxLength={255}
          isRequired={true}
        />
      </FormGroup>
      <FormGroup>
        <TextField
          label="Server URL"
          placeholder="https://mcp.example.com/mcp"
          {...serverUrlInput}
          maxLength={2048}
          isRequired={true}
        />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Transport Type (Optional)" />
        <SelectDropdown
          displayName={transportType || 'Auto-detect'}
          menu={
            <Menu value={transportType}>
              <Option
                value={null}
                onSelect={() => {
                  setTransportType(null);
                }}
              >
                Auto-detect
              </Option>
              <Option
                value="STREAMABLE_HTTP"
                onSelect={() => {
                  setTransportType('STREAMABLE_HTTP');
                }}
              >
                Streamable HTTP
              </Option>
              <Option
                value="SSE"
                onSelect={() => {
                  setTransportType('SSE');
                }}
              >
                SSE
              </Option>
            </Menu>
          }
        />
      </FormGroup>
    </Modal>
  );
}
